<template>
  <div>
    <HeadingSection
      class="border-gpx_gray-800 mx-[24px] mb-[35px] border-t pt-[28px] sm:mx-0 sm:px-0 sm:pt-[34px]"
      heading="Deposits"
      desc="Deposits owed against your commitments"
      :is-empty-data="isEmpty"
    />
    <DataTable
      v-if="!isEmpty"
      :loading="loading"
      :list="rows"
      :columns="columns"
    />
    <DataTableNav v-if="meta && !isEmpty" v-model="pagination" :meta="meta" />
    <EmptyState
      v-if="isEmpty"
      class="mt-[35px]"
      type="table"
      heading="There are no deposits yet"
      desc="They will appear here once a commitment is confirmed."
    />
  </div>
</template>

<script setup>
import { ref, computed, toRef } from 'vue';
import { getAccountPayments } from '@gpx-spa/queries';
import { TableResultsPerPage } from '@gpx-spa/constants';
import DataTable from '@gpx-spa/ui/src/elements/DataTable.vue';
import DataTableNav from '@gpx-spa/ui/src/elements/DataTableNav.vue';
import HeadingSection from '@gpx-spa/ui/src/elements/HeadingSection.vue';
import EmptyState from '@gpx-spa/ui/src/components/EmptyState.vue';
import { formatMoney, formatTime } from '@/filters';

const props = defineProps({
  accountId: {
    type: Number,
    required: true,
  },
});

const columns = ['Deposit', 'Commitment', 'Due Date', 'Status', 'Amount'];

const pagination = ref({ page: 1, per_page: TableResultsPerPage[0] });

const params = computed(() => ({
  owner: 'commitment',
  page: pagination.value.page,
  per_page: pagination.value.per_page,
}));

const { data, isLoading: loading } = getAccountPayments(
  toRef(props, 'accountId'),
  params,
);

const meta = computed(() => data.value?.meta || null);

const rows = computed(() =>
  (data.value?.data || []).map((payment) => ({
    id: payment.id,
    number: `#${payment.id}`,
    commitment: `C#${payment.commitment_id}`,
    due: payment.due_date ? formatTime(payment.due_date, 'MMM d, yyyy') : '—',
    status: {
      status: payment.status === 'succeeded' ? 'paid' : payment.status,
    },
    amount: `$${formatMoney(payment.amount)}`,
  })),
);

const isEmpty = computed(() => rows.value.length === 0 && !loading.value);
</script>
